import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../stores/useUserStore';
import { useRBAC } from '../hooks/useRBAC';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Save,
  DollarSign,
  Calendar,
  Tag,
  CreditCard,
  FileText,
  Check,
  ChevronsUpDown,
  Camera,
  Image as ImageIcon,
  X,
  Upload,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { nekoPos } from 'public-contracts';
import CostService from '../services/costService';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { cn } from '../utils';
import { useFileUpload } from '../hooks/useFileUpload';
import { CameraCaptureDialog } from '../components/CameraCaptureDialog';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';

interface CostFormData {
  description: string;
  amount: number;
  categoryId: string;
  method: string;
  date: string;
  notes: string;
  receipt?: string;
}

type Cost = nekoPos.Cost;
type CostCategory = nekoPos.CostCategory;

const ModernCostCreate: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();
  const { highestRole } = useRBAC();
  const costService = useMemo(() => new CostService(), []);
  const { uploadFile, getFileUrl } = useFileUpload();
  const { formatCurrency, settings } = useCurrencyFormat();

  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState<string>('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [amountDisplay, setAmountDisplay] = useState<string>('');

  const methods = useForm<CostFormData>({
    defaultValues: {
      description: '',
      amount: 0,
      categoryId: '',
      method: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      receipt: '',
    },
  });

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');

  // Clear search when popover opens
  useEffect(() => {
    if (categoryOpen) {
      setCategorySearch('');
    }
  }, [categoryOpen]);

  const selectedCategoryId = watch('categoryId');
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const receiptId = watch('receipt');
  const amountValue = watch('amount');

  // Sync amount display when form value changes externally (e.g., form reset)
  useEffect(() => {
    if (amountValue !== undefined && amountValue !== null) {
      const numValue = amountValue || 0;
      if (numValue === 0) {
        setAmountDisplay('');
        return;
      }
      // Format the number with separators
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: settings.precision,
      }).format(numValue);
      
      const withCustomSeparators = formatted
        .replace(/,/g, settings.thousandsSeparator || ',')
        .replace(/\./g, settings.decimalSeparator || '.');
      
      setAmountDisplay(withCustomSeparators);
    }
  }, [amountValue, settings.thousandsSeparator, settings.decimalSeparator, settings.precision]);

  // Load receipt preview when receipt ID changes
  useEffect(() => {
    const loadReceiptPreview = async () => {
      if (receiptId) {
        try {
          const url = await getFileUrl(receiptId);
          setReceiptPreview(url || '');
        } catch (error) {
          console.error('Error loading receipt preview:', error);
          setReceiptPreview('');
        }
      } else {
        setReceiptPreview('');
      }
    };
    loadReceiptPreview();
  }, [receiptId, getFileUrl]);

  // Load real categories - fetch all for search
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const categoriesData = await costService.getCostCategories({
          page: 1,
          perPage: 1000, // Fetch all categories for dropdown/search
        });
        const categoriesArray = Array.isArray(categoriesData)
          ? categoriesData
          : [];
        setCategories(categoriesArray);
        console.log(
          `✅ Loaded ${categoriesArray.length} categories (total: ${
            (categoriesData as any).__total || categoriesArray.length
          })`
        );
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [costService]);

  const hasPermission = (action: 'create' | 'edit' | 'delete') => {
    if (!user?.roles) return false;
    if (user.roles.includes('sysAdmin')) return true;
    if (user.roles.includes('admin')) return true;
    if (user.roles.includes('staff')) return true;
    if (user.roles.includes('storeManager')) return true;
    if (user.roles.includes('cashier')) {
      return action !== 'delete';
    }
    return false;
  };

  if (!hasPermission('create')) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {translate('pos.common.accessDenied', 'Access Denied')}
          </p>
          <p className="text-muted-foreground">
            {translate(
              'pos.costs.create.no_permission',
              'You do not have permission to create cost entries.'
            )}
          </p>
        </div>
      </div>
    );
  }

  const handleReceiptUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingReceipt(true);
    try {
      const fileId = await uploadFile(file);
      if (fileId) {
        setValue('receipt', fileId);
        const url = await getFileUrl(fileId);
        setReceiptPreview(url || '');
      }
    } catch (error) {
      console.error('Error uploading receipt:', error);
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleRemoveReceipt = () => {
    setValue('receipt', '');
    setReceiptPreview('');
  };

  const startCamera = () => {
    setShowCameraDialog(true);
  };

  const handleCameraCapture = async (file: File) => {
    setUploadingReceipt(true);
    try {
      const fileId = await uploadFile(file);
      if (fileId) {
        setValue('receipt', fileId);
        const url = await getFileUrl(fileId);
        setReceiptPreview(url || '');
      }
    } catch (error) {
      console.error('Error uploading receipt:', error);
    } finally {
      setUploadingReceipt(false);
    }
  };

  const onSubmit = async (data: CostFormData) => {
    setSaving(true);
    try {
      const costData: Omit<Cost, 'id'> = {
        description: data.description,
        amount: data.amount,
        categoryId: data.categoryId,
        method: data.method || undefined,
        date: new Date(data.date),
        notes: data.notes || undefined,
        receipt: data.receipt || undefined,
      };

      await costService.createCost(costData);

      // Redirect to dashboard for staff, costs list for managers/admins
      if (highestRole === 'staff') {
        navigate('/dashboard');
      } else {
        navigate('/costs');
      }
    } catch (error) {
      console.error('Error creating cost:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Navigate to dashboard for staff, costs list for managers/admins
    if (highestRole === 'staff') {
      navigate('/dashboard');
    } else {
      navigate('/costs');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2 mx-auto"></div>
          <span className="text-muted-foreground">
            {translate('pos.common.loading')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="border-border text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {translate('pos.common.action.back', 'Back')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {translate('pos.costs.create.title', 'Create Cost Entry')}
            </h1>
            <p className="text-muted-foreground">
              {translate(
                'pos.costs.create.subtitle',
                'Add a new expense to track'
              )}
            </p>
          </div>
        </div>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-3 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {translate(
                      'pos.costs.create.basic_information',
                      'Basic Information'
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label
                      htmlFor="description"
                      className="text-sm font-medium"
                    >
                      {translate('pos.costs.fields.description', 'Description')}{' '}
                      *
                    </Label>
                    <Input
                      id="description"
                      placeholder={translate(
                        'pos.costs.create.description_placeholder',
                        'Enter cost description...'
                      )}
                      {...register('description', {
                        required: translate(
                          'pos.costs.create.description_required',
                          'Description is required'
                        ),
                      })}
                      className="mt-1"
                    />
                    {errors.description && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.description.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="amount" className="text-sm font-medium">
                        {translate('pos.costs.fields.amount', 'Amount')} *
                      </Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="amount"
                          type="text"
                          placeholder={translate(
                            'pos.costs.create.amount_placeholder',
                            '0.00'
                          )}
                          value={amountDisplay}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Remove all non-numeric characters except decimal separator
                            const numericValue = inputValue
                              .replace(
                                new RegExp(
                                  `[^0-9${settings.decimalSeparator}]`,
                                  'g'
                                ),
                                ''
                              )
                              .replace(
                                new RegExp(
                                  `\\${settings.thousandsSeparator}`,
                                  'g'
                                ),
                                ''
                              );
                            
                            // Handle decimal separator
                            const parts = numericValue.split(settings.decimalSeparator);
                            let cleanValue = parts[0];
                            if (parts.length > 1) {
                              // Limit decimal places based on precision
                              const decimals = parts[1].slice(0, settings.precision);
                              cleanValue = `${parts[0]}${settings.decimalSeparator}${decimals}`;
                            }
                            
                            // Parse to number
                            const numValue = parseFloat(
                              cleanValue.replace(
                                new RegExp(`\\${settings.decimalSeparator}`, 'g'),
                                '.'
                              )
                            ) || 0;
                            
                            // Update form value
                            setValue('amount', numValue, { shouldValidate: true });
                            
                            // Format for display
                            if (cleanValue === '' || cleanValue === '0') {
                              setAmountDisplay('');
                            } else {
                              // Format the number with separators
                              const formatted = new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: settings.precision,
                              }).format(numValue);
                              
                              const withCustomSeparators = formatted
                                .replace(/,/g, settings.thousandsSeparator || ',')
                                .replace(/\./g, settings.decimalSeparator || '.');
                              
                              setAmountDisplay(withCustomSeparators);
                            }
                          }}
                          onBlur={() => {
                            // Ensure display is formatted on blur
                            const numValue = amountValue || 0;
                            if (numValue === 0) {
                              setAmountDisplay('');
                              return;
                            }
                            // Format the number with separators
                            const formatted = new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: settings.precision,
                            }).format(numValue);
                            
                            const withCustomSeparators = formatted
                              .replace(/,/g, settings.thousandsSeparator || ',')
                              .replace(/\./g, settings.decimalSeparator || '.');
                            
                            setAmountDisplay(withCustomSeparators);
                          }}
                          className="pl-10"
                        />
                      </div>
                      {errors.amount && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.amount.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="date" className="text-sm font-medium">
                        {translate('pos.costs.fields.date', 'Date')} *
                      </Label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="date"
                          type="date"
                          {...register('date', {
                            required: translate(
                              'pos.costs.create.date_required',
                              'Date is required'
                            ),
                          })}
                          className="pl-10"
                        />
                      </div>
                      {errors.date && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.date.message}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Category and Method */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    {translate(
                      'pos.costs.create.category_payment',
                      'Category & Payment'
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="categoryId"
                        className="text-sm font-medium"
                      >
                        {translate('pos.costs.fields.category', 'Category')} *
                      </Label>
                      <Popover
                        open={categoryOpen}
                        onOpenChange={setCategoryOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={categoryOpen}
                            className={cn(
                              'w-full justify-between mt-1 pl-10',
                              !selectedCategoryId && 'text-muted-foreground'
                            )}
                          >
                            <div className="flex items-center">
                              <Tag className="absolute left-3 h-4 w-4 text-muted-foreground" />
                              <span className="ml-2">
                                {selectedCategory
                                  ? selectedCategory.name
                                  : translate(
                                      'pos.costs.create.select_category',
                                      'Select a category'
                                    )}
                              </span>
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput
                              placeholder={translate(
                                'pos.costs.create.search_categories',
                                'Search categories...'
                              )}
                              value={categorySearch}
                              onValueChange={setCategorySearch}
                            />
                            <CommandList>
                              <CommandEmpty>
                                {translate(
                                  'pos.costs.create.no_category_found',
                                  'No category found.'
                                )}
                              </CommandEmpty>
                              <CommandGroup>
                                {Array.isArray(categories) &&
                                  categories.map((category) => (
                                    <CommandItem
                                      key={category.id}
                                      value={`${category.name} ${
                                        category.description || ''
                                      }`}
                                      onSelect={() => {
                                        setValue('categoryId', category.id, {
                                          shouldValidate: true,
                                        });
                                        setCategoryOpen(false);
                                        setCategorySearch('');
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          'mr-2 h-4 w-4',
                                          selectedCategoryId === category.id
                                            ? 'opacity-100'
                                            : 'opacity-0'
                                        )}
                                      />
                                      {category.name}
                                    </CommandItem>
                                  ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {errors.categoryId && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.categoryId.message}
                        </p>
                      )}
                      <input
                        type="hidden"
                        {...register('categoryId', {
                          required: translate(
                            'pos.costs.create.category_required',
                            'Category is required'
                          ),
                        })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="method" className="text-sm font-medium">
                        {translate(
                          'pos.costs.fields.payment_method',
                          'Payment Method'
                        )}
                      </Label>
                      <Select
                        value={watch('method') || ''}
                        onValueChange={(value) =>
                          setValue('method', value === 'none' ? '' : value)
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <div className="flex items-center">
                            <CreditCard className="mr-2 h-4 w-4 text-muted-foreground" />
                            <SelectValue
                              placeholder={translate(
                                'pos.costs.create.payment_method_placeholder',
                                'Select payment method'
                              )}
                            />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            {translate(
                              'pos.costs.create.payment_method_none',
                              'None'
                            )}
                          </SelectItem>
                          <SelectItem value="cash">
                            {translate(
                              'pos.costs.create.payment_method_cash',
                              'Cash'
                            )}
                          </SelectItem>
                          <SelectItem value="bank">
                            {translate(
                              'pos.costs.create.payment_method_bank',
                              'Bank Transfer'
                            )}
                          </SelectItem>
                          <SelectItem value="credit_card">
                            {translate(
                              'pos.costs.create.payment_method_credit_card',
                              'Credit Card'
                            )}
                          </SelectItem>
                          <SelectItem value="vnpay">
                            {translate(
                              'pos.costs.create.payment_method_vnpay',
                              'VnPay'
                            )}
                          </SelectItem>
                          <SelectItem value="momo">
                            {translate(
                              'pos.costs.create.payment_method_momo',
                              'MoMo'
                            )}
                          </SelectItem>
                          <SelectItem value="zalopay">
                            {translate(
                              'pos.costs.create.payment_method_zalopay',
                              'ZaloPay'
                            )}
                          </SelectItem>
                          <SelectItem value="other">
                            {translate(
                              'pos.costs.create.payment_method_other',
                              'Other'
                            )}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Receipt */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    {translate('pos.costs.create.receipt', 'Receipt')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {receiptPreview ? (
                      <div className="relative">
                        <img
                          src={receiptPreview}
                          alt={translate(
                            'pos.costs.create.receipt_preview',
                            'Receipt preview'
                          )}
                          className="w-full max-h-96 object-contain rounded-md border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRemoveReceipt}
                          className="absolute top-2 right-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={uploadingReceipt}
                          asChild
                        >
                          <label>
                            {uploadingReceipt ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                {translate(
                                  'pos.costs.create.uploading_receipt',
                                  'Uploading...'
                                )}
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                {translate(
                                  'pos.costs.create.upload_receipt',
                                  'Upload Receipt'
                                )}
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleReceiptUpload}
                            />
                          </label>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={uploadingReceipt}
                          onClick={startCamera}
                        >
                          {uploadingReceipt ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                              {translate(
                                'pos.costs.create.uploading_receipt',
                                'Uploading...'
                              )}
                            </>
                          ) : (
                            <>
                              <Camera className="h-4 w-4 mr-2" />
                              {translate(
                                'pos.costs.create.capture_receipt',
                                'Capture Receipt'
                              )}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {translate(
                        'pos.costs.create.receipt_description',
                        'Upload or capture a photo of the receipt (optional)'
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {translate(
                      'pos.costs.create.additional_notes',
                      'Additional Notes'
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="notes" className="text-sm font-medium">
                      {translate('pos.costs.fields.notes', 'Notes')}
                    </Label>
                    <Textarea
                      id="notes"
                      placeholder={translate(
                        'pos.costs.create.notes_placeholder',
                        'Add any additional notes or details...'
                      )}
                      {...register('notes')}
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-4">
                {/* Summary Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {translate('pos.costs.create.summary', 'Summary')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costs.fields.amount', 'Amount')}:
                      </span>
                      <span className="font-medium">
                        {formatCurrency(watch('amount') || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costs.fields.category', 'Category')}:
                      </span>
                      <span className="font-medium">
                        {(Array.isArray(categories) &&
                          categories.find((c) => c.id === watch('categoryId'))
                            ?.name) ||
                          translate(
                            'pos.costs.create.not_selected',
                            'Not selected'
                          )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costs.fields.date', 'Date')}:
                      </span>
                      <span className="font-medium">
                        {watch('date') ||
                          translate(
                            'pos.costs.create.not_selected',
                            'Not selected'
                          )}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <Button type="submit" className="w-full" disabled={saving}>
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {translate('pos.common.action.saving', 'Saving...')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {translate(
                            'pos.costs.create.save_button',
                            'Save Cost Entry'
                          )}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      className="w-full border-border text-foreground hover:bg-muted"
                    >
                      {translate('pos.common.action.cancel', 'Cancel')}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      {/* Camera Dialog */}
      <CameraCaptureDialog
        open={showCameraDialog}
        onClose={() => setShowCameraDialog(false)}
        onCapture={handleCameraCapture}
        title={translate('pos.costs.create.capture_receipt', 'Capture Receipt')}
        description={translate(
          'pos.costs.create.camera_instructions',
          'Position the receipt in front of the camera and click Capture when ready'
        )}
        captureButtonText={translate(
          'pos.costs.create.capture_button',
          'Capture'
        )}
      />
    </div>
  );
};

export default ModernCostCreate;
